import { Component, Input, OnInit } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { CustomQuestion, EntityCall, JoiEvent, Ranking } from 'globals/store/events.data';
import { entityTypesReselector } from '../../store/reselectors/entity.reselector';
import { CoreEntityType, ShareableItemType } from 'globals/store/share.data';
import { Wrapper } from '../wrapper.component';
import { DotMenuComponent, DotMenuEvent, DotMenuItem } from '../misc/dot-menu.component';
import { ConfirmDeleteData, openConfirmDeleteGetResult } from '../dialog.confirmDelete.component';
import { readOnlyReselector } from '../../store/reselectors/permissions.reselector';
import { submitterTemplatesReselector } from '../../store/reselectors/events.reselector';
import { traceNames, TraceNameType } from '../../services/performance.tracking.data';
import { ShareCodeComponent } from 'src/app/modules/share/share-code.component';
import { TitleToggleComponent } from '../wrappers/title-toggle-wrapper.component';
import { InputEditorComponent } from '../joi-input/input-editor';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { EntityTypeDropdownComponent } from './entity-type-dropdown.component';
import { TipTextComponent } from '../misc/tip-text.component';
import { JoiInputComponent } from '../joi-input/joi-input.component';
import { createId } from 'globals/helpers/helpers';

@Component({
    selector: 'submitted-entities',
    template: `
<tip-text [type]="'tip'" [head]="'Tip:'" 
  [body]="'Create a webform for ' + entityType + ' submissions. Share on websites, eDM’s and social media. Submissions will automatically appear in the ' + entityType + ' tables'">
</tip-text>
<div class="submitter-holder" *ngFor="let template of submitTemplates; trackBy: trackById">
  <div class="top">
    <joi-input 
      [(model)]="template.name" 
      type="textarea" 
      [placeholder]="'Submission name...'" 
      (joiChange)="updateTemplates()" 
      [disabled]="readOnly"
    ></joi-input>
    <type-select-dropdown *ngIf="entityType"
      [noneSelectedText]="'No type selected'"
      [label]="(entityType | titlecase) + ' Type: '"
      [types]="entityTypesMap"
      [entityType]="entityType"
      [selectedId]="template.entityGroup"
      (selectId)="updateSubmittedType(template, $event)"
    ></type-select-dropdown>
    <dot-menu [dotMenuItems]="dotMenuItems" (select)="dotMenuSelection($event, template)"></dot-menu>
  </div>

  <input-editor
    [object]="template" 
    [prop]="'instructions'"
    [placeholder]="'Add instructions for submission...'"
    (changed)="instructionsChanged(template)"
  ></input-editor>
  <title-toggle-wrapper
    *ngIf="!!event"
    [title]="(entityType | titlecase) + ' submission webform'"
    [hideBottom]="!template.on"
    [toggleOn]="!!template.on"
    (toggle)="toggleCanSubmit(template)"
  >
    <div style="margin-bottom: 20px">
      SHAREABLE WEBFORM:
    </div>
    <share-code 
      [shareType]="shareType"
      [shareCode]="event.eventId + '/' + template.id" 
      [hideTitle]="true"
      [disabled]="false"
      [hideJson]="true"
      [selectedStatus]=""
    >
    </share-code>
    <div class="hint" style="margin-top: 10px;">
      This link can be used multiple times to submit multiple {{ entityType }}s.
    </div>
  </title-toggle-wrapper>
</div> 


<button class="secondary add-question" (click)="addTemplate()">+ ADD SUBMISSION</button>
`,
    styleUrls: ['./submitted-entities.scss'],
    standalone: true,
    imports: [ShareCodeComponent, TitleToggleComponent, InputEditorComponent, TitleCasePipe, DotMenuComponent, EntityTypeDropdownComponent, TipTextComponent, JoiInputComponent, CommonModule],
})
export class SubmittedEntitiesComponent extends Wrapper<SubmittedEntitiesComponent> implements OnInit {
  @Input() entityType: CoreEntityType
  entityTypesMap: Dictionary<Ranking> = {}
  event: JoiEvent
  submitTemplates: EntityCall[] = []
  dotMenuItems: DotMenuItem[] = [{ id: 'delete', title: 'Delete' }]
  shareType: ShareableItemType
  traceName: TraceNameType
  traceNames = traceNames

  readOnly: boolean = true

  ngOnInit() {

    this.selectProp(submitterTemplatesReselector, this.entityType, 'submitTemplates')

    console.log({ entityType: this.entityType })
    switch (this.entityType) {
      case 'attendee':
        this.shareType = 'submitAttendees'
        this.traceName = this.traceNames.viewSubmittedExhibitors
        break
      case 'performer':
        this.shareType ='submitPerformers'
        this.traceName = this.traceNames.viewSubmittedPerformers
        break
      case 'resource':
        this.shareType = 'submitResources'
        this.traceName = this.traceNames.viewSubmittedResources
        break
      case 'exhibitor':
        this.shareType = 'submitExhibitors'
        this.traceName = this.traceNames.viewSubmittedExhibitors
        break
      default:
        console.log("No handling for " + this.entityType)
        return
    }

    this.selectCallback(entityTypesReselector, this.entityType, (data: any) => {
      if (!data) return
      this.entityTypesMap = data.typesMap
    })
  }

  constructor() {
    super()
    this.subProp(this.store.select('events', 'event'), 'event')
    this.selectProp(readOnlyReselector, null, 'readOnly')
    
  }

  updateSubmittedType(template: EntityCall, id: string) {
    this.submitTemplates.find(t => t.id === template.id).entityGroup = id || null
    this.eventService.updateEntitySubmitters(this.submitTemplates, this.entityType)
  }

  instructionsChanged(template: EntityCall) {
    console.log("CHANGED!", template, this.submitTemplates)
    this.updateTemplates()
  }


  toggleCanSubmit(template: EntityCall) {
    this.submitTemplates.find(t => t.id === template.id).on = !this.submitTemplates.find(t => t.id === template.id).on
    this.eventService.updateEntitySubmitters(this.submitTemplates, this.entityType)
  }

  async dotMenuSelection({ id, event }: DotMenuEvent, template: EntityCall) {
    const data: ConfirmDeleteData = {
      title: `Delete ${this.entityType} submissions`,
      rect: (event.target as HTMLButtonElement).getBoundingClientRect(),
      dialog: this.dialog
    }
    const del = await openConfirmDeleteGetResult(data)
    if (!del) return

    const submitTemplates = this.submitTemplates.filter(t => t.id !== template.id)
    this.eventService.updateEntitySubmitters(submitTemplates, this.entityType)
  }


  getEntityTypes() {
    switch (this.entityType) {
      case 'performer':
        return this.event.performerTypes
      case 'resource':
        return this.event.resourceTypes
      case 'exhibitor':
        return this.event.exhibitorTypes
      default:
        console.log(`Add question not implemented for ${this.entityType}s yet!`)
        return
    }
  }

  updateTemplates() {
    this.eventService.updateEntitySubmitters(this.submitTemplates, this.entityType)
  }

  async addTemplate() {
    this.submitTemplates.push({ id: createId(), name: '' })
    this.eventService.updateEntitySubmitters(this.submitTemplates, this.entityType)
  }

}

